/**
 * «Написать врачу» — ярлыки мессенджеров из контактов карточки.
 *
 * Поля Telegram и WhatsApp в справочнике пустые у ВСЕХ врачей: заполняют
 * только телефон. Поэтому ссылка строится по такому правилу:
 *
 *   1. вставили готовую ссылку — открываем её как есть;
 *   2. написали ник — подставляем в шаблон площадки;
 *   3. не написали ничего — берём телефон, по которому WhatsApp и Telegram
 *      и находят человека.
 *
 * Готовая ссылка идёт первой не случайно: вид ссылок у площадок меняется, а
 * скопированная из самого приложения работает всегда — даже если наш шаблон
 * устареет.
 *
 * МАКС ЖИВЁТ ПО ДРУГИМ ПРАВИЛАМ. Ссылки на чат по номеру телефона у него нет
 * вовсе — ни `max.ru/79…`, ни подобия `wa.me` (проверено: такие адреса
 * отдают 404). Человека там находят поиском по номеру или фамилии внутри
 * самого приложения, а поделиться можно только личной ссылкой вида
 * `max.ru/u/…` — но её заводят себе не все, и у обычного врача её нет нигде:
 * в его карточке «Поделиться» пусто.
 *
 * Зато свой адрес есть у ЛЮБОГО чата в веб-версии: `web.max.ru/c/123456`.
 * Отсюда три случая:
 *
 *   • записан адрес чата из веб-версии — приложение открывает ровно этот чат
 *     (`max://chat/id/123456`); единственный способ, работающий для всех;
 *   • записана личная ссылка или ник — открываем профиль через `max://max.ru/…`;
 *   • записан только телефон — открываем приложение на списке чатов и кладём
 *     номер в буфер обмена: остаётся вставить его в поиск. Открыть чат прямо
 *     по номеру нельзя: такого пути у МАКСа нет — проверено и на сайте, и в
 *     настольном приложении.
 *
 * ЯРЛЫКИ СТОЯТ ВСЕГДА, все три. Те, которым некуда вести, — бледные и не
 * нажимаются, а подсказка говорит, чего не хватает. Прежде недоступные просто
 * не показывались, и ряд менял длину от врача к врачу: у одного три значка, у
 * другого один, у третьего пусто — карточка каждый раз выглядела иначе, и не
 * было видно, что у врача не заполнено.
 *
 * Подписи рядом со значками НЕТ: фирменные ярлыки узнаются сразу, а слово
 * «WhatsApp» рядом с зелёной трубкой повторяло то же самое второй раз и
 * занимало место, которого в карточке заказа нет. Название остаётся во
 * всплывающей подсказке и в `aria-label` — для мыши и для чтения с экрана.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { сенсорный } from './pointer.js';

export interface Контакты {
  phone: string;
  telegram: string;
  whatsapp: string;
  max: string;
}

type Вид = 'wa' | 'tg' | 'max';

/** Номер в том виде, в каком его ждут ссылки: только цифры, страна — семёркой. */
function номерТелефона(значение: string, phone: string): string {
  const цифры = (значение || phone || '').replace(/\D/g, '');
  /* «8 914…» и «+7 914…» — один номер, а ссылка нужна в одном виде. */
  return цифры.length === 11 && цифры.startsWith('8') ? `7${цифры.slice(1)}` : цифры;
}

/** Похоже ли записанное на ник, а не на номер. */
function этоНик(текст: string): boolean {
  const ник = текст.replace(/^@/, '');
  return ник.length > 0 && !/^[\d+()\s-]+$/.test(ник);
}

/**
 * Ссылка на чат в МАКСе — только из того, что записано в поле «МАКС».
 *
 * Телефон сюда не годится: чата по номеру в МАКСе не открыть. Схема `max://`
 * ведёт сразу в настольное приложение, минуя браузер.
 */
export function максСсылка(значение: string): string | null {
  const текст = (значение ?? '').trim();
  if (!текст) return null;

  /*
   * АДРЕС ЧАТА ИЗ ВЕБ-ВЕРСИИ — главный способ.
   *
   * Личная ссылка (`max.ru/u/…`) есть далеко не у каждого: она появляется,
   * только если человек завёл себе имя в профиле. У обычного врача её нет
   * нигде, и «Поделиться» в его карточке пусто.
   *
   * Зато свой адрес есть у ЛЮБОГО чата: `web.max.ru/c/123456`. Открыли
   * переписку в браузере, скопировали адрес — и настольное приложение открывает
   * ровно этот чат по `max://chat/id/123456`. Эту же схему МАКС использует
   * у себя, когда переводит из уведомления в окно приложения.
   */
  const чат = /^(?:https?:\/\/)?(?:web\.)?max\.ru\/c\/(\d+)/i.exec(текст) ?? /^c\/(\d+)$/i.exec(текст);
  if (чат) return `max://chat/id/${чат[1]}`;

  /* Личная ссылка на профиль — если она всё-таки есть. */
  const адрес = текст.match(/^(?:https?:\/\/)?(?:www\.)?max\.(?:ru|me)\/(.+)$/i);
  if (адрес) return `max://max.ru/${адрес[1]}`;

  if (этоНик(текст)) return `max://max.ru/${текст.replace(/^@/, '')}`;
  return null;
}

export function чатСсылка(вид: Вид, значение: string, phone: string): string | null {
  const текст = (значение ?? '').trim();
  if (вид === 'max') return максСсылка(текст);

  if (/^https?:\/\//i.test(текст)) return текст;
  const номер = номерТелефона(текст, phone);
  const естьНомер = номер.length >= 10;
  if (вид === 'wa') return естьНомер ? `https://wa.me/${номер}` : null;
  if (этоНик(текст)) return `https://t.me/${текст.replace(/^@/, '')}`;
  return естьНомер ? `https://t.me/+${номер}` : null;
}

/**
 * Адрес для НАСТОЛЬНОГО приложения.
 *
 * Обычные ссылки `wa.me` и `t.me` на компьютере открывают браузер, а он уже
 * предлагает перейти в приложение — лишняя страница и лишнее нажатие. У обоих
 * мессенджеров есть своя схема, по которой окно приложения открывается сразу:
 *
 *   whatsapp://send?phone=79149286966
 *   tg://resolve?domain=ник      (или ?phone=79149286966)
 *
 * На ТЕЛЕФОНЕ это не нужно: там `wa.me` и `t.me` и так открывают приложение,
 * а схема в мобильном браузере иногда ведёт в пустую вкладку.
 */
export function схемаПриложения(вид: Вид, значение: string, phone: string): string | null {
  const текст = (значение ?? '').trim();
  if (вид === 'max') return максСсылка(текст);

  /* Готовую ссылку не разбираем: в ней может быть приглашение в группу. */
  if (/^https?:\/\//i.test(текст)) return null;

  const номер = номерТелефона(текст, phone);
  const естьНомер = номер.length >= 10;
  if (вид === 'wa') return естьНомер ? `whatsapp://send?phone=${номер}` : null;
  if (этоНик(текст)) return `tg://resolve?domain=${текст.replace(/^@/, '')}`;
  return естьНомер ? `tg://resolve?phone=${номер}` : null;
}

/*
 * Рисунки взяты у самих площадок: WhatsApp и Telegram — официальные контуры
 * (simple-icons), МАКС обведён по его же значку с max.ru. Заливка белая на
 * фирменном фоне — ровно так ярлыки выглядят на телефоне, и врач узнаёт их
 * не читая.
 */

/** Трубка в облачке — знак WhatsApp. */
function ЗнакWhatsApp() {
  return (
    <svg viewBox="0 0 24 24" fill="#fff" aria-hidden width="60%" height="60%">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

/**
 * Бумажный самолётик — знак Telegram.
 *
 * Рамка обрезана по самому самолётику: в полной сетке 24×24 он занимает едва
 * половину, и рядом с трубкой WhatsApp выглядел крошкой.
 */
function ЗнакTelegram() {
  return (
    <svg viewBox="4.1 4 13.7 11.8" fill="#fff" aria-hidden width="64%" height="64%">
      <path d="M16.906 4.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

/**
 * Облачко в облачке — знак МАКСа.
 *
 * Обведён по значку с max.ru: большое облако с хвостиком влево-вниз, внутри
 * вырезано второе, поменьше, со своим хвостиком. Вырез сделан правилом
 * `evenodd`, поэтому сквозь внутреннее облачко просвечивает фон ярлыка —
 * как на самом значке.
 */
function ЗнакMax() {
  return (
    <svg viewBox="0 0 24 24" fill="#fff" fillRule="evenodd" aria-hidden width="72%" height="72%">
      <path d="M3.3 10.5A9 9 0 1 1 7.14 18.67C6.1 20.3 5.2 21.4 4.2 21.45c-.75.05-1.05-.45-1-1.25.08-2.3.1-6.8.1-9.7Zm4.57 1.88a4.5 4.5 0 1 1 3.27 3.57L8.3 17Z" />
    </svg>
  );
}

const ЯРЛЫКИ: { ключ: Вид; имя: string; фон: string; знак: ReactNode; поле: (c: Контакты) => string }[] = [
  { ключ: 'wa', имя: 'WhatsApp', фон: '#25D366', знак: <ЗнакWhatsApp />, поле: (c) => c.whatsapp },
  {
    ключ: 'tg',
    имя: 'Telegram',
    фон: 'linear-gradient(180deg,#37BBFE,#007DBB)',
    знак: <ЗнакTelegram />,
    поле: (c) => c.telegram,
  },
  {
    ключ: 'max',
    имя: 'МАКС',
    фон: 'linear-gradient(45deg,#35C2F5 0%,#4B3BF2 45%,#B23BEF 100%)',
    знак: <ЗнакMax />,
    поле: (c) => c.max,
  },
];

/** Почему ярлык бледный: чего не хватает в карточке врача. */
function почемуНельзя(вид: Вид, contact?: Контакты): string {
  if (!contact) return 'Врач не выбран';
  if (вид === 'max') return 'Не записана ссылка на профиль в МАКСе — чата по номеру там нет';
  return 'У врача не записан ни номер телефона, ни ник';
}

export function Messengers({
  contact,
  size = 30,
}: {
  /** Контакты врача. Пусто — врач ещё не выбран, ярлыки бледные. */
  contact?: Контакты;
  /** Сторона ярлыка. В карточке заказа места меньше, чем в справочнике. */
  size?: number;
}) {
  /* Подсказка после нажатия на МАКС без ссылки — гаснет сама. */
  const [подсказка, setПодсказка] = useState<string | null>(null);
  const таймер = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(таймер.current), []);
  const сказать = (текст: string) => {
    window.clearTimeout(таймер.current);
    setПодсказка(текст);
    таймер.current = window.setTimeout(() => setПодсказка(null), 4000);
  };

  const номер = номерТелефона('', contact?.phone ?? '');
  /*
   * Ярлыки стоят ВСЕГДА — все три.
   *
   * Раньше показывались только те, которым было куда вести, и место под ними
   * то появлялось, то пропадало: у одного врача три значка, у другого один, у
   * третьего пусто, и карточка каждый раз выглядела иначе. Теперь ряд на месте
   * всегда, а недоступные ярлыки бледные и не нажимаются — сразу видно, чего
   * врачу не хватает, и куда вписать недостающее.
   */
  const кнопки = ЯРЛЫКИ.map((я) => {
    const ссылка = contact ? чатСсылка(я.ключ, я.поле(contact), contact.phone) : null;
    /*
     * МАКС без личной ссылки всё равно работает: открыть приложение и положить
     * номер в буфер — это ровно то, что делают руками, только без переписывания
     * цифр из карточки.
     */
    const поиском = я.ключ === 'max' && !ссылка && номер.length >= 10;
    const схема = contact ? схемаПриложения(я.ключ, я.поле(contact), contact.phone) : null;
    return { ...я, ссылка, схема, поиском, почему: почемуНельзя(я.ключ, contact) };
  });

  const вид = (фон: string, доступен: boolean) => ({
    width: size,
    height: size,
    /* Скруглением в четверть стороны ярлык и выглядит ярлыком. */
    borderRadius: Math.round(size * 0.28),
    background: фон,
    boxShadow: доступен ? '0 1px 2px rgba(28,27,24,0.25)' : 'none',
    /* Обесцвеченный и бледный — «здесь могло бы быть, но нечем». */
    filter: доступен ? undefined : 'grayscale(1)',
    opacity: доступен ? undefined : 0.35,
  });
  const общее = 'inline-flex items-center justify-center transition-transform hover:-translate-y-0.5';

  /**
   * Нажатие на ПК: сперва настольное приложение, браузер — запасной путь.
   *
   * Уходим по схеме `whatsapp://` или `tg://`; если через полторы секунды
   * страница по-прежнему в фокусе — значит, приложения нет и его никто не
   * открыл, тогда открываем обычную ссылку в браузере. Потеря фокуса —
   * единственный доступный признак: браузер не сообщает, удалась ли передача
   * адреса другой программе.
   *
   * На телефоне обработчик не вмешивается: там `wa.me` и `t.me` сами
   * открывают приложение, а схема иногда роняет пустую вкладку.
   */
  const открытьВПриложении = (
    event: React.MouseEvent<HTMLAnchorElement>,
    схема: string | null,
    ссылка: string,
  ) => {
    if (сенсорный() || !схема) return;
    event.preventDefault();
    window.location.href = схема;
    window.setTimeout(() => {
      if (document.hasFocus()) window.open(ссылка, '_blank', 'noreferrer');
    }, 1500);
  };

  const искатьВМаксе = async () => {
    let скопировано = false;
    try {
      await navigator.clipboard.writeText(`+${номер}`);
      скопировано = true;
    } catch {
      // Буфер может быть закрыт настройками браузера — тогда просто покажем номер.
    }
    сказать(
      скопировано
        ? `Номер +${номер} скопирован — вставьте в поиск МАКСа`
        : `Найдите в МАКСе по номеру ${contact?.phone.trim() || `+${номер}`}`,
    );
    /*
     * Открываем СПИСОК ЧАТОВ — там же строка поиска.
     *
     * Пробовали открывать чат прямо по номеру () — на
     * рабочем компьютере владельца это чата не открыло: у МАКСа такого пути
     * нет ни на сайте, ни в приложении. Оставлен проверенный путь: окно
     * приложения плюс номер в буфере.
     */
    window.location.href = 'max://max.ru/:chat-list';
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {кнопки.map((я) =>
        !я.ссылка && !я.поиском ? (
          /*
           * Бледный ярлык — не ссылка и не кнопка, а просто значок: нажимать
           * нечего, и подсказка объясняет, чего не хватает.
           */
          <span
            key={я.ключ}
            title={я.почему}
            aria-label={`${я.имя}: ${я.почему}`}
            className="inline-flex cursor-not-allowed items-center justify-center"
            style={вид(я.фон, false)}
          >
            {я.знак}
          </span>
        ) : я.поиском ? (
          <button
            key={я.ключ}
            type="button"
            onClick={искатьВМаксе}
            title={
              `Открыть МАКС и скопировать номер. Чат сразу не откроется: у МАКСа нет адреса чата по номеру. ` +
              `Чтобы открывался — откройте переписку с врачом на web.max.ru и вставьте адрес вида ` +
              `web.max.ru/c/123456 в поле «МАКС» в карточке врача.`
            }
            aria-label="Открыть МАКС и скопировать номер"
            className={общее}
            style={вид(я.фон, true)}
          >
            {я.знак}
          </button>
        ) : (
          <a
            key={я.ключ}
            href={я.ссылка as string}
            onClick={(e) => открытьВПриложении(e, я.схема, я.ссылка as string)}
            /* Своя схема открывает приложение прямо здесь; пустая вкладка ни к чему. */
            target={я.ключ === 'max' ? undefined : '_blank'}
            rel="noreferrer"
            title={`Написать в ${я.имя}`}
            aria-label={`Написать в ${я.имя}`}
            className={общее}
            style={вид(я.фон, true)}
          >
            {я.знак}
          </a>
        ),
      )}
      {подсказка && <span className="text-[11px] text-ink2">{подсказка}</span>}
    </span>
  );
}
